import { isPrivateAssessmentLocation, isPrivateTrackingContext } from "@/utils/trackingPrivacy";
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
// PERFORMANCE: Firebase auth is now dynamically imported only when needed
// This prevents loading ~100KB of Firebase auth module on public pages (signup, login)
import { safeGetItem, safeRemoveItem } from "@/utils/storage";
import { savePostLoginRedirect } from "@/helpers/postLoginRedirect";
// PERFORMANCE: Lazy-load Vuex modules for admin routes
import store, { ensureModules, preloadModules } from "@/store";
import { decodeAssessmentResultsUserId } from "@/utils/assessmentResultsLink";
import {
  ASSESSMENT_RESULTS_ROUTE_MODULES,
  CANDIDATE_ASSESSMENT_ROUTE_MODULES,
  isAssessmentResultsRoutePath,
  isReportSharePath,
} from "@/utils/assessmentResultsRouteModules";
// PERFORMANCE: Route-aware prefetching to reduce 125+ requests to ~15-20 for candidates
import {
  handleRoutePrefetch,
  shouldLoadThaiFonts,
  loadThaiFonts,
} from "@/utils/prefetch";
import { hasAuthenticatedSession, isPublicAuthPath } from "@/utils/routeFlags";
import { markInAppNavigationStart } from "@/utils/funnelTiming";

// PERFORMANCE: Lazy-load mixpanel to avoid blocking initial route evaluation
const mixpanelEnabled = process.env.VUE_APP_MIXPANEL_ENABLED == "on";
const mixpanelToken = String(process.env.VUE_APP_MIXPANEL_TOKEN || "");
const mixpanelDebug = process.env.VUE_APP_MIXPANEL_DEBUG == "on";
let mixpanelInstance: any = null;

async function getMixpanel() {
  if (!mixpanelInstance && mixpanelEnabled && mixpanelToken) {
    const mixpanelModule = await import("mixpanel-browser");
    mixpanelInstance = mixpanelModule.default;
    mixpanelInstance.init(mixpanelToken, { debug: mixpanelDebug });
  }
  return mixpanelInstance;
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Default",
    // Lazy-load assessments page for better initial bundle size
    component: () =>
      import(
        /* webpackChunkName: "assessments" */ "../views/MyAssessments.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "assessments",
      auth: true,
      title: "My Assessments | My Culture",
    },
  },
  // Preview route for thank you page (dev only) - must be before catch-all
  {
    path: "/preview/thank-you",
    name: "PreviewThankYou",
    component: () =>
      import(
        /* webpackChunkName: "assessment-test" */ "../views/AssessmentTest.vue"
      ),
    meta: {
      navbar: false,
      title: "Thank You Preview | My Culture",
      preview: "complete",
    },
  },
  ...(process.env.NODE_ENV === "development"
    ? [
        {
          path: "/preview/classic-report-v3", name: "PreviewClassicReportV3",
          component: () => import("../views/ClassicReportPreview.vue"),
          meta: { navbar: false, title: "New Report Internal Preview | MyCulture", publicAuth: true },
        },
        {
          path: "/preview/big-five", name: "PreviewBigFive",
          component: () => import("../views/BigFivePreview.vue"),
          meta: { navbar: false, title: "Big Five Preview | MyCulture", publicAuth: true },
        },
        {
          path: "/preview/values-group", name: "PreviewValuesGroup",
          component: () => import("../views/ValuesGroupPreview.vue"),
          meta: { navbar: false, title: "Values Group Preview | MyCulture", publicAuth: true },
        },
        {
          path: "/preview/results-report",
          name: "PreviewResultsReport",
          component: () =>
            import(
              /* webpackChunkName: "results-report-preview" */ "../views/ResultsReportDesignPreview.vue"
            ),
          meta: {
            navbar: false,
            title: "Results Report Design Preview | MyCulture",
            publicAuth: true,
          },
        },
      ]
    : []),
  {
    path: "/:code/:value?",
    name: "RedirectPage",
    component: () =>
      import(
        /* webpackChunkName: "redirect-page" */ "../views/RedirectPage.vue"
      ),
    meta: {
      navbar: false,
      title: "Redirect Page | My Culture",
    },
  },
  {
    path: "/login",
    name: "Login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/Login/Login.vue"),
    meta: {
      navbar: false,
      title: "Login | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/th/login",
    name: "Login_th",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/Login/Login.vue"),
    meta: {
      navbar: false,
      title: "เข้าสู่ระบบ | My Culture",
      lang: "TH",
      publicAuth: true,
    },
  },
  {
    path: "/signup",
    name: "Signup",
    component: () =>
      import(/* webpackChunkName: "signup" */ "../views/Login/Signup.vue"),
    meta: {
      navbar: false,
      title: "Sign Up | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/th/signup",
    name: "Signup_th",
    component: () =>
      import(/* webpackChunkName: "signup" */ "../views/Login/Signup.vue"),
    meta: {
      navbar: false,
      title: "สร้างบัญชีใหม่ | My Culture",
      lang: "TH",
      publicAuth: true,
    },
  },
  {
    path: "/signup/verify",
    name: "SignupVerify",
    component: () =>
      import(/* webpackChunkName: "verify" */ "../views/Login/Pincode.vue"),
    meta: {
      navbar: false,
      title: "Verify PIN | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/account/verify",
    name: "AccountVerify",
    component: () =>
      import(/* webpackChunkName: "verify" */ "../views/Login/Pincode.vue"),
    meta: {
      navbar: false,
      title: "Verify PIN | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/signup/info",
    name: "SignupInfo",
    component: () =>
      import(/* webpackChunkName: "signup-info" */ "../views/Login/Info.vue"),
    meta: {
      navbar: false,
      title: "Your Info | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/signup/branding",
    name: "SignupBranding",
    component: () =>
      import(
        /* webpackChunkName: "signup-branding" */ "../views/Login/Branding.vue"
      ),
    meta: {
      navbar: false,
      title: "Your Branding | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/confirm-canditate",
    name: "ConfirmCanditate",
    component: () =>
      import(
        /* webpackChunkName: "confirm-canditate" */ "../views/ConfirmCandidate.vue"
      ),
    meta: {
      navbar: false,
      title: "Confirm your profile | My Culture",
    },
  },
  {
    path: "/pincode",
    name: "Pincode",
    component: () =>
      import(/* webpackChunkName: "signup" */ "../views/Login/Pincode.vue"),
    meta: {
      navbar: false,
      title: "Pincode | My Culture",
      publicAuth: true,
    },
  },
  {
    path: "/assessments",
    name: "Assessments",
    // Lazy-load assessments page for better initial bundle size
    component: () =>
      import(
        /* webpackChunkName: "assessments" */ "../views/MyAssessments.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "assessments",
      auth: true,
      title: "My Assessments | My Culture",
    },
  },
  {
    path: "/assessments/assessments",
    name: "AssessmentsAssessmentsDetails",
    component: () =>
      import(
        /* webpackChunkName: "AssessmentsDetails" */ "../views/Assessments/AssessmentsDetails.vue"
      ),
    meta: {
      navbar: false,
      navbarName: "assessments",
      auth: true,
      title: "My Assessments | My Culture",
    },
  },
  {
    path: "/assessments/candidate/report",
    name: "CandidateReportViewer",
    component: () => import("../views/Assessments/Candidate.vue"),
    meta: { navbar: false, navbarName: "assessments", auth: true, title: "Assessment report" },
  },
  {
    path: "/assessments/candidate",
    name: "AssessmentsCandidate",
    component: () =>
      import(
        /* webpackChunkName: "AssessmentsCandidate" */ "../views/Assessments/Candidate.vue"
      ),
    meta: {
      navbar: false,
      navbarName: "assessments",
      auth: true,
      title: "My Culture",
    },
  },
  {
    path: "/assessments/create/:step",
    name: "AssessmentsCreate",
    component: () =>
      import(
        /* webpackChunkName: "assessments-create" */ "../views/Assessments/Create.vue"
      ),
    meta: {
      navbar: false,
      navbarName: "assessments",
      auth: true,
      title: "Create a New Assessment | My Culture",
    },
  },
  {
    path: "/assessments/create-ai-readiness",
    name: "CreateAIReadinessAssessment",
    component: () =>
      import(
        /* webpackChunkName: "assessments-create" */ "../views/Assessments/Create.vue"
      ),
    meta: {
      navbar: false,
      navbarName: "assessments",
      auth: true,
      title: "Create AI Readiness Assessment | My Culture",
      aiReadinessQuickStart: true,
    },
  },
  {
    path: "/guest/assessments/create/:step",
    name: "GuestAssessmentsCreate",
    component: () =>
      import(
        /* webpackChunkName: "assessments-create" */ "../views/Assessments/Create.vue"
      ),
    meta: {
      navbar: false,
      navbarName: "assessments",
      auth: true,
      title: "Create a New Assessment | My Culture",
    },
  },
  {
    path: "/assessment/create/set-up/values-and-traits",
    name: "AssessmentsCreateSetUpTaluesAndTraits",
    component: () =>
      import(
        /* webpackChunkName: "setupValuesAndTraits" */ "../views/Assessments/Create/SetupValues&Traits.vue"
      ),
    meta: {
      navbar: false,
      auth: true,
      title: "Set Up Values Alignment | My Culture",
    },
  },
  {
    path: "/assessment/create/set-up/acceptable-behaviors",
    name: "SetupAcceptableBehaviors",
    component: () =>
      import(
        /* webpackChunkName: "SetupAcceptableBehaviors" */ "../views/Assessments/Create/SetupAcceptableBehaviors.vue"
      ),
    meta: {
      navbar: false,
      auth: true,
      title: "Setup Acceptable Behaviors | My Culture",
    },
  },
  {
    path: "/assessment/create/set-up/OAIC-archetypes",
    name: "SetupOAICArchetypes",
    component: () =>
      import(
        /* webpackChunkName: "SetupOAICArchetypes" */ "../views/Assessments/Create/SetupOAICArchetypes.vue"
      ),
    meta: {
      navbar: false,
      auth: true,
      title: "Setup Culture Profile | My Culture",
    },
  },
  //Custom Survey
  {
    path: "/assessment/create/set-up/custom-surveys",
    name: "SetUpCustomSurvey",
    component: () =>
      import(
        /* webpackChunkName: "SetUpCustomSurvey" */ "../views/Assessments/Create/SetupCustomSurveys.vue"
      ),
    meta: {
      navbar: false,
      auth: true,
      title: "Setup Custom Surveys | My Culture",
    },
  },
  {
    path: "/assessment/edit/:routeId",
    name: "AssessmentsEdit",
    component: () =>
      import(
        /* webpackChunkName: "assessments-create" */ "../views/Assessments/Create.vue"
      ),
    meta: {
      navbar: false,
      navbarName: "assessments",
      auth: true,
      title: "Edit an Assessment | My Culture",
    },
  },
  {
    path: "/assessment/:respondentId",
    name: "AssessmentByRespondent",
    component: () =>
      import(
        /* webpackChunkName: "respondent-assessment-entry" */ "../views/RespondentAssessmentEntry.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment | My Culture",
    },
  },
  {
    path: "/assessment-test",
    name: "AssessmentTest",
    component: () =>
      import(
        /* webpackChunkName: "assessment-test" */ "../views/AssessmentTest.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Test | My Culture",
    },
  },
  {
    path: "/assessment-test/:id/:params",
    name: "AssessmentTestForCandidate",
    component: () =>
      import(
        /* webpackChunkName: "assessment-test" */ "../views/AssessmentTest.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Test | My Culture",
    },
  },
  {
    path: "/assessment-test/:code",
    name: "AssessmentTestForCandidateSecure",
    component: () =>
      import(
        /* webpackChunkName: "assessment-test" */ "../views/AssessmentTest.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Test | My Culture",
    },
  },
  {
    path: "/respondents",
    name: "Respondents",
    component: () =>
      import(
        /* webpackChunkName: "respondents" */ "../views/MyRespondents.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "respondents",
      auth: true,
      title: "My Respondents | My Culture",
    },
  },
  {
    path: "/account",
    name: "MyAccount",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/MyAccount.vue"),
    meta: {
      navbar: false,
      auth: true,
      title: "My Account | My Culture",
    },
  },
  {
    path: "/account/plan-billing/success",
    name: "PaymentSuccess",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/Payment/Success.vue"),
    meta: {
      navbar: false,
      auth: true,
      title: "My Account | Plan & Billing - Payment Success",
    },
  },
  {
    path: "/account/my-team/:teamId",
    name: "MyAccountWithTeamId",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/MyAccount.vue"),
    meta: {
      navbar: false,
      auth: true,
      title: "My Account | My Culture",
    },
  },
  {
    path: "/account/:tab",
    name: "MyAccountWithTab",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/MyAccount.vue"),
    meta: {
      navbar: false,
      auth: true,
      title: "My Account | My Culture",
    },
  },
  {
    path: "/docs-for-dev",
    name: "DocsForDev",
    component: () =>
      import(/* webpackChunkName: "docs-for-dev" */ "../views/DocsForDev.vue"),
    meta: {
      title: "Docs | My Culture",
    },
  },
  {
    path: "/component-showcase",
    name: "ComponentShowcase",
    component: () =>
      import(
        /* webpackChunkName: "component-showcase" */ "../views/ComponentShowcase.vue"
      ),
    meta: {
      title: "Component Showcase | My Culture",
    },
  },
  {
    path: "/component-showcase-simple",
    name: "ComponentShowcaseSimple",
    component: () =>
      import(
        /* webpackChunkName: "component-showcase-simple" */ "../views/ComponentShowcaseSimple.vue"
      ),
    meta: {
      title: "Simple Component Showcase | My Culture",
    },
  },
  {
    path: "/test",
    name: "TestPage",
    component: () => import("../views/TestPage.vue"),
    meta: {
      title: "Test Page",
    },
  },
  {
    path: "/assessment-results",
    name: "AssessmentResults",
    component: () =>
      import(
        /* webpackChunkName: "assessment-results" */ "../views/AssessmentResults.vue"
      ),
    meta: {
      navbar: false,
      title: "My Culture",
    },
  },
  {
    path: "/assessment-group-analytics",
    name: "AssessmentGroupAnalytics",
    component: () =>
      import(
        /* webpackChunkName: "assessment-group-analytics" */ "../views/Assessments/AssessmentGroupAnalytics.vue"
      ),
    meta: {
      navbar: false,
      title: "Group Analytics | My Culture",
    },
  },
  {
    path: "/th/assessment-results",
    name: "AssessmentResultsTH",
    component: () =>
      import(
        /* webpackChunkName: "assessment-results" */ "../views/AssessmentResults.vue"
      ),
    meta: {
      navbar: false,
      title: "My Culture",
      lang: "TH",
    },
  },
  {
    path: "/en/assessment-results",
    name: "AssessmentResultsEN",
    component: () =>
      import(
        /* webpackChunkName: "assessment-results" */ "../views/AssessmentResults.vue"
      ),
    meta: {
      navbar: false,
      title: "My Culture",
      lang: "EN",
    },
  },
  // Public report share links: same view as /assessment-results, but the
  // report tier is decided by the ?t= token instead of the u_id alone.
  {
    path: "/report",
    name: "ReportShare",
    component: () =>
      import(
        /* webpackChunkName: "assessment-results" */ "../views/AssessmentResults.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Report",
    },
  },
  {
    path: "/th/report",
    name: "ReportShareTH",
    component: () =>
      import(
        /* webpackChunkName: "assessment-results" */ "../views/AssessmentResults.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Report",
      lang: "TH",
    },
  },
  {
    path: "/en/report",
    name: "ReportShareEN",
    component: () =>
      import(
        /* webpackChunkName: "assessment-results" */ "../views/AssessmentResults.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Report",
      lang: "EN",
    },
  },
  {
    path: "/core-values-analyzer",
    name: "CoreValuesAnalyzer",
    component: () =>
      import(
        /* webpackChunkName: "core-values-analyzer" */ "../views/CoreValuesAnalyzer.vue"
      ),
    meta: {
      navbar: false,
      title: "Core Values Analyzer | My Culture",
    },
  },
  {
    path: "/en/core-values-analyzer",
    name: "CoreValuesAnalyzerEN",
    component: () =>
      import(
        /* webpackChunkName: "core-values-analyzer" */ "../views/CoreValuesAnalyzer.vue"
      ),
    meta: {
      navbar: false,
      title: "Core Values Analyzer | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/core-values-analyzer",
    name: "CoreValuesAnalyzerTH",
    component: () =>
      import(
        /* webpackChunkName: "core-values-analyzer" */ "../views/CoreValuesAnalyzer.vue"
      ),
    meta: {
      navbar: false,
      title: "Core Values Analyzer | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/job-role-generator",
    name: "JobRoleGenerator",
    component: () =>
      import(
        /* webpackChunkName: "job-role-generator1" */ "../views/JobRoleGenerator.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Job Role Generator | My Culture",
    },
  },
  {
    path: "/en/job-role-generator",
    name: "JobRoleGeneratorEN",
    component: () =>
      import(
        /* webpackChunkName: "job-role-generator2" */ "../views/JobRoleGenerator.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Job Role Generator | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/job-role-generator",
    name: "JobRoleGeneratorTH",
    component: () =>
      import(
        /* webpackChunkName: "job-role-generator3" */ "../views/JobRoleGenerator.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Job Role Generator | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/job-post-generator",
    name: "JobPostGenerator",
    component: () =>
      import(
        /* webpackChunkName: "job-post-generator1" */ "../views/JobPostGenerator.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Job Post Generator | My Culture",
    },
  },
  {
    path: "/en/job-post-generator",
    name: "JobPostGeneratorEN",
    component: () =>
      import(
        /* webpackChunkName: "job-post-generator2" */ "../views/JobPostGenerator.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Job Post Generator | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/job-post-generator",
    name: "JobPostGeneratorTH",
    component: () =>
      import(
        /* webpackChunkName: "job-post-generator3" */ "../views/JobPostGenerator.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Job Post Generator | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/interview-guide",
    name: "InterviewGuide",
    component: () =>
      import(
        /* webpackChunkName: "interview-guide1" */ "../views/InterviewGuide.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Interview Guide | My Culture",
    },
  },
  {
    path: "/en/interview-guide",
    name: "InterviewGuideEN",
    component: () =>
      import(
        /* webpackChunkName: "interview-guide2" */ "../views/InterviewGuide.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Interview Guide | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/interview-guide",
    name: "InterviewGuideTH",
    component: () => import("../views/InterviewGuide.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Interview Guide | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/career-path-tracker",
    name: "CareerPathTracker",
    component: () => import("../views/CareerPathTracker.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Career Path Tracker | My Culture",
    },
  },
  {
    path: "/en/career-path-tracker",
    name: "CareerPathTrackerEN",
    component: () => import("../views/CareerPathTracker.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Career Path Tracker | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/career-path-tracker",
    name: "CareerPathTrackerTH",
    component: () => import("../views/CareerPathTracker.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Career Path Tracker | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/okr-generator",
    name: "OkrGenerator",
    component: () => import("../views/OkrGenerator.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "OKR Generator | My Culture",
    },
  },
  {
    path: "/en/okr-generator",
    name: "OkrGeneratorEN",
    component: () => import("../views/OkrGenerator.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "OKR Generator | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/okr-generator",
    name: "OkrGeneratorTH",
    component: () => import("../views/OkrGenerator.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "OKR Generator | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/performance-improvement",
    name: "PerformanceImprovement",
    component: () => import("../views/PerformanceImprovement.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Performance Improvement | My Culture",
    },
  },
  {
    path: "/en/performance-improvement",
    name: "PerformanceImprovementEN",
    component: () => import("../views/PerformanceImprovement.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Performance Improvement | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/performance-improvement",
    name: "PerformanceImprovementTH",
    component: () => import("../views/PerformanceImprovement.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Performance Improvement | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/day-plan",
    name: "NumDayPlan",
    component: () => import("../views/NumDayPlan.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Performance Improvement | My Culture",
    },
  },
  {
    path: "/en/day-plan",
    name: "NumDayPlanEN",
    component: () => import("../views/NumDayPlan.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Performance Improvement | My Culture",
      lang: "EN",
    },
  },
  {
    path: "/th/day-plan",
    name: "NumDayPlanTH",
    component: () => import("../views/NumDayPlan.vue"),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      toolbox: true,
      title: "Performance Improvement | My Culture",
      lang: "TH",
    },
  },
  {
    path: "/guest/assessments/create",
    name: "GuestSetup",
    component: () => import("../views/Guest/Setup.vue"),
    meta: {
      navbar: false,
      title: "Guest Setup | My Culture",
    },
  },
  {
    path: "/core-values",
    name: "CoreValues",
    component: () =>
      import(
        /* webpackChunkName: "my-core-values" */ "../views/MyCoreValues.vue"
      ),
    meta: {
      navbar: true,
      navbarName: "core-values",
      auth: true,
      title: "My Core Values | My Culture",
    },
  },
  // {
  //   path: "/setup",
  //   name: "Setup",
  //   component: () =>
  //     import(/* webpackChunkName: "setup" */ "../views/Setup.vue"),
  //   meta: {
  //     title: "My Culture",
  //   },
  // },
  // ==========================================
  // PUBLIC AI READINESS ASSESSMENT ROUTES
  // ==========================================
  {
    path: "/ai-readiness",
    name: "AIReadinessLanding",
    component: () =>
      import(
        /* webpackChunkName: "ai-readiness" */ "../views/PublicAIAssessment/Landing.vue"
      ),
    meta: {
      navbar: false,
      title: "Free AI Readiness Assessment - Test Your AI Skills | My Culture",
      description:
        "Take our free 10-15 minute AI readiness assessment. Get scored on 5 key AI collaboration skills, compare with others on our leaderboard, and receive personalized improvement tips.",
    },
  },
  {
    path: "/ai-readiness/start",
    name: "AIReadinessAssessment",
    component: () =>
      import(
        /* webpackChunkName: "ai-readiness" */ "../views/PublicAIAssessment/Assessment.vue"
      ),
    meta: {
      navbar: false,
      title: "AI Readiness Assessment in Progress | My Culture",
    },
  },
  {
    path: "/ai-readiness/complete",
    name: "AIReadinessComplete",
    component: () =>
      import(
        /* webpackChunkName: "ai-readiness" */ "../views/PublicAIAssessment/Complete.vue"
      ),
    meta: {
      navbar: false,
      title: "Assessment Complete - See Your AI Score | My Culture",
    },
  },
  {
    path: "/ai-readiness/results/:id",
    name: "AIReadinessResults",
    component: () =>
      import(
        /* webpackChunkName: "ai-readiness" */ "../views/PublicAIAssessment/Results.vue"
      ),
    meta: {
      navbar: false,
      title: "Your AI Readiness Results & Skills Breakdown | My Culture",
    },
  },
  {
    path: "/ai-readiness/leaderboard",
    name: "AIReadinessLeaderboard",
    component: () =>
      import(
        /* webpackChunkName: "ai-readiness" */ "../views/PublicAIAssessment/Leaderboard.vue"
      ),
    meta: {
      navbar: false,
      title: "AI Readiness Leaderboard - Top Performers | My Culture",
      description:
        "See who ranks highest in AI collaboration skills. Compare your AI readiness score with professionals across different roles and industries.",
    },
  },
  // ==========================================
  // ERROR PAGES
  // ==========================================
  {
    path: "/404",
    name: "pageNotFound404",
    component: () => import(/* webpackChunkName: "404" */ "../views/404.vue"),
    meta: {
      title: "Page Not Found | My Culture",
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "pageNotFound",
    component: () => import(/* webpackChunkName: "404" */ "../views/404.vue"),
    meta: {
      title: "Page Not Found | My Culture",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

const ADMIN_ROUTE_MODULES = [
  "routes",
  "user",
  "team",
  "assessment",
  "assessmentSection",
  "assessmentTemplate",
  "respondent",
  "caches",
  "big5",
] as const;

function isCandidateAssessmentPath(path: string): boolean {
  return (
    path.includes("/assessment-test") ||
    path.includes("/confirm-canditate") ||
    path.includes("/assessment-results") ||
    path.includes("/assessments/candidate") ||
    path.includes("/preview/thank-you") ||
    path.includes("/assessment-group-analytics") ||
    // The share route renders AssessmentResults.vue and is public: without
    // this its store modules would never register (MYCULTURE-4V).
    isReportSharePath(path)
  );
}

// Same test vue-router uses to tell a component from a lazy loader.
function isLazyRouteComponent(component: unknown): component is () => Promise<unknown> {
  return (
    typeof component === "function" &&
    !("displayName" in component) &&
    !("props" in component) &&
    !("__vccOpts" in component)
  );
}

/**
 * Start downloading a route's component and store module chunks without
 * waiting for anything. Nothing is registered or rendered here; the guard
 * and the router still do that, they just find the code already loading.
 *
 * Components carry CSS, which is inserted as soon as their chunk loads, so
 * they are only warmed once the global styles are in (`components: false`
 * before that, at startup). Pages that need a session are not warmed for
 * logged-out visitors, who are about to be sent to login.
 */
export function warmRouteDependencies(
  target: string | RouteLocationNormalized,
  { components = true }: { components?: boolean } = {}
): void {
  try {
    const route = typeof target === "string" ? router.resolve(target) : target;
    const path = route.path.toLowerCase();
    const needsSession = route.matched.some((record) => record.meta?.auth);
    if (needsSession && !hasAuthenticatedSession()) {
      return;
    }

    if (components) {
      for (const record of route.matched) {
        for (const component of Object.values(record.components || {})) {
          if (isLazyRouteComponent(component)) {
            component().catch(() => undefined);
          }
        }
      }
    }

    if (components && path.startsWith("/assessment-test/")) {
      // The entry form is an async child of AssessmentTest; fetch it with the
      // page rather than after the page renders.
      import(
        /* webpackChunkName: "confirm-canditate" */ "../views/ConfirmCandidate.vue"
      ).catch(() => undefined);
    }
    if (isCandidateAssessmentPath(path)) {
      preloadModules(
        isAssessmentResultsRoutePath(path)
          ? ASSESSMENT_RESULTS_ROUTE_MODULES
          : CANDIDATE_ASSESSMENT_ROUTE_MODULES
      );
    } else if (needsSession) {
      preloadModules(ADMIN_ROUTE_MODULES);
    }
  } catch {
    // Warming is an optimisation only.
  }
}

router.beforeEach(async (to, from, next) => {
  const path = to.path.toLowerCase();

  // A loaded tag container cannot be unloaded safely. Enter a fresh document
  // before placing private identifiers in browser history or analytics events.
  if (isPrivateAssessmentLocation(to.fullPath) && from.matched.length > 0 &&
      !isPrivateAssessmentLocation(from.fullPath)) {
    window.location.replace(to.fullPath);
    return;
  }

  // Track page view with mixpanel (lazy-loaded on first route change)
  if (
    mixpanelEnabled &&
    !isPrivateTrackingContext(to.fullPath) &&
    to.meta &&
    to.meta.title &&
    !to.meta?.publicAuth
  ) {
    getMixpanel().then((mp) => {
      if (mp && !isPrivateTrackingContext(window.location.pathname + window.location.search)) {
        const title = String(to.meta.title || "Untitled");
        mp.track(title);
      }
    });
  }

  // Start the page's own code downloading now; otherwise it waits for every
  // store module below before the router asks for it.
  warmRouteDependencies(to);
  if (from.matched.length) {
    markInAppNavigationStart();
  }

  // PERFORMANCE: Lazy-load admin Vuex modules based on route requirements
  // This reduces initial bundle by ~150-200KB for candidate assessment pages
  const isCandidateAssessmentRoute = isCandidateAssessmentPath(path);
  const isGuestRoute = path.includes("/guest/");
  const isPublicAuthRoute = isPublicAuthPath(path);

  // Sessions that boot on a public auth page skip auth/setUserLogin in
  // App.vue created(); SPA navigations out of those pages would otherwise
  // reach authed views with auth.user still null (no account menu, and
  // actions reading rootState.auth.user crash).
  if (
    !isPublicAuthRoute &&
    !store.getters["auth/user"] &&
    safeGetItem("userData")
  ) {
    await store.dispatch("auth/setUserLogin");
  }

  if (path.includes("/signup/branding")) {
    await ensureModules(["user"]);
  }

  if (isGuestRoute) {
    await ensureModules(["guest", "user", "team"]);
  }

  // Load public AI readiness assessment module
  if (path.includes("/ai-readiness")) {
    await ensureModules(["publicAiAssessment"]);
  }

  // Candidate-facing assessment flows still rely on the heavier store modules,
  // but they do not need them on public auth pages.
  if (isCandidateAssessmentRoute) {
    const routeModules = isAssessmentResultsRoutePath(path)
      ? ASSESSMENT_RESULTS_ROUTE_MODULES
      : CANDIDATE_ASSESSMENT_ROUTE_MODULES;
    await ensureModules([...routeModules]);

    // PERFORMANCE: results pages — start the Firestore reads now so they
    // overlap the route-chunk download and component mount instead of
    // waiting for MainCardResults to initialize.
    if (
      path.includes("/assessment-results") ||
      path.includes("/assessments/candidate") ||
      isReportSharePath(path)
    ) {
      const resultsId = decodeAssessmentResultsUserId(to.query?.u_id);
      // Share links carry no as_id — the form id is already the first
      // segment of the doc id, so derive it there and keep the same check.
      const asId =
        String(to.query?.as_id || "").trim() ||
        (isReportSharePath(path) && resultsId ? resultsId.split("-")[0] : "");
      if (!isReportSharePath(path) && asId && resultsId && resultsId.split("-")[0] === asId) {
        Promise.resolve(
          store.dispatch("assessment/prefetchResults", { _id: resultsId })
        ).catch(() => undefined);
      }
      // Newer records store only Big Five scores; the card needs the report
      // text library to turn them into dimensions, so fetch it alongside.
      Promise.resolve(store.dispatch("big5/preloadResultText")).catch(
        () => undefined
      );
    }
  }

  // Core values analyzer also serves anonymous visitors (/core-values-analyzer
  // has no meta.auth), so its module must load outside the auth branch below
  if (path.includes("core-values")) {
    await ensureModules(["coreValuesAnalyzer"]);
  }

  // Load admin modules for authenticated admin routes. Without a session the
  // guard below sends the visitor to login, so don't make them wait for
  // these downloads first.
  if (to.meta?.auth && hasAuthenticatedSession()) {
    await ensureModules([...ADMIN_ROUTE_MODULES]);

    // Load billing modules for account/billing pages
    if (path.includes("/account") || path.includes("/billing")) {
      await ensureModules([
        "quota",
        "payment",
        "coupon",
        "referral",
        "customDomain",
      ]);
    }

    // Load AI toolbox modules for toolbox routes
    if (to.meta?.toolbox) {
      await ensureModules(["aiProxy", "aiService", "toolbox"]);

      // Load specific generator modules based on route
      if (path.includes("job-role")) await ensureModules(["jobRoleGenerator"]);
      if (path.includes("job-post")) await ensureModules(["jobPostGenerator"]);
      if (path.includes("career-path"))
        await ensureModules(["careerPathTracker"]);
      if (path.includes("okr")) await ensureModules(["okrGenerator"]);
      if (path.includes("performance-improvement"))
        await ensureModules(["performanceImprovement"]);
      if (path.includes("day-plan")) await ensureModules(["numDayPlan"]);
      if (path.includes("interview")) await ensureModules(["interviewGuide"]);
    }

    // Load assessment template modules for assessment management
    if (path.includes("/assessments")) {
      await ensureModules(["quota", "aiAssessmentTemplate"]);
    }

    // Overlap assessments list bootstrap with route chunk download (same pattern as results prefetch).
    if (path === "/assessments") {
      Promise.resolve(
        store.dispatch("assessmentTemplate/bootstrapAssessmentsPage")
      ).catch(() => undefined);
    }

    if (path === "/respondents") {
      Promise.resolve(
        store.dispatch("respondent/bootstrapRespondentsPage")
      ).catch(() => undefined);
    }
  }

  if (isPublicAuthRoute) {
    // Public auth routes intentionally avoid admin/user bootstrap unless a later
    // step explicitly needs it.
    next();
    return;
  }

  if (to.matched.some((record) => record.meta.auth)) {
    // because of this project has social login
    // NOT same MY Happily so that can't use fix login
    /// and force to accesss this page
    const userDataEncoded = safeGetItem("userData") || "";
    let valid = false;
    try {
      const userData = JSON.parse(atob(userDataEncoded) || "{}");
      if (userData && userData.email) {
        valid = true;
      }
    } catch (e) {
      console.error(e);
    }

    if (!valid) {
      // Dynamic import - only load Firebase when actually needed for signout
      const { getAuth } = await import("firebase/auth");
      const auth = getAuth();
      safeRemoveItem("userData");
      auth.signOut();
      if (to.path == "/core-values") {
        next({
          path: "/core-values-analyzer",
          query: to.query,
        });
      } else {
        // need it to signup page
        if (to.meta.toolbox) {
          const parts = to.path.split("/");
          const lang = parts.length > 2 ? parts[1] : "";
          const prefix = lang.length > 0 ? `/${lang}` : "";
          next({
            path: `${prefix}/signup`,
            // query: { key: to.path },
            query: { tool: parts.pop() },
          });
        } else {
          const redirectUrl = savePostLoginRedirect(to.fullPath);
          store.commit("auth/redirectUrl", redirectUrl);
          next({
            path: "/login",
            query: { return_to: redirectUrl },
          });
        }
      }
    } else {
      if (to.path == "/") {
        next({
          path: "/assessments",
        });
      } else {
        next();
      }
    }
  } else {
    next();
  }
});

// PERFORMANCE: Route-aware prefetching
// Only prefetch chunks relevant to the current route category
// This reduces candidate assessment requests from 125+ to ~15-20
router.afterEach((to) => {
  // Handle selective prefetching based on route
  handleRoutePrefetch(to.path);

  // Load Thai fonts on-demand when navigating to Thai routes
  // This avoids loading 2 extra font files for non-Thai users
  const query = to.query as Record<string, string>;
  if (shouldLoadThaiFonts(to.path, query)) {
    loadThaiFonts();
  }

});

export default router;
